/**
 * Environment access, validated once at startup.
 *
 * The previous frontend fell back to a hardcoded production IP when
 * VITE_API_BASE_URL was unset, so a misconfigured build silently pointed at
 * someone else's backend. Missing configuration now fails loudly instead.
 */

function required(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `${name} is not set. Copy .env.example to .env and fill it in before starting the app.`
    );
  }
  return value.trim().replace(/\/+$/, '');
}

const useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';

/** Origin of the backend, with no trailing slash and no path. */
export const API_ORIGIN: string = useMockApi
  ? 'http://localhost:8000'
  : required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL);

export const API_BASE_URL = `${API_ORIGIN}/api/v1`;

/**
 * The WebSocket router is mounted at the application root, not under /api/v1,
 * so it derives from the origin rather than the API base.
 */
export const WS_ORIGIN = API_ORIGIN.replace(/^http/, 'ws');

export const USE_MOCK_API = useMockApi;

/**
 * Firebase web config. Absent by default: push is opt-in per deployment, and the
 * previous version had these values committed to the repository.
 */
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const firebaseVapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

export const isPushConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseVapidKey
);

/**
 * CARTO Basemaps key. Optional: without it the tiles still render, just with an
 * "API key required" watermark, so a missing value degrades rather than breaks.
 *
 * Not a secret in the way the Firebase service account is — a basemap key ships in
 * the client bundle by design and is visible in any network tab. It is still
 * deployment configuration, so it is not hardcoded in source: restrict it by domain
 * in the CARTO dashboard rather than trying to hide it.
 */
export const cartoApiKey: string | undefined =
  import.meta.env.VITE_CARTO_API_KEY?.trim() || undefined;
